import React, { useEffect, useState } from "react";
import {
  Box,
  Icon,
  KeyValuePairs,
  Popover,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import {
  OmsDailyScheduleUtc,
  OmsProduct,
} from "../../lusca-search-api/generated-src";

// These UTC start times are the existing producer schedules, not completion estimates.
const scheduledHour: Record<OmsProduct, number> = {
  channels: 15,
  addon: 0,
  tvod: 11,
};
const dayMs = 24 * 60 * 60 * 1000;

export function reportTimestamp(value: string | number): string {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "Refresh time unavailable";
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(date);
}

export function scheduledRefresh(
  product: OmsProduct,
  now: number,
  dailySchedule?: OmsDailyScheduleUtc
) {
  const last = new Date(now);
  last.setUTCHours(
    dailySchedule?.hour ?? scheduledHour[product],
    dailySchedule?.minute ?? 0,
    0,
    0
  );
  if (last.getTime() > now) last.setUTCDate(last.getUTCDate() - 1);
  return { last: last.getTime(), next: last.getTime() + dayMs };
}

export default function OMSReportSchedule({
  product,
  sourceRefreshedAt,
  dailySchedule,
}: {
  product: OmsProduct;
  sourceRefreshedAt: string;
  dailySchedule?: OmsDailyScheduleUtc | null;
}) {
  const [now, setNow] = useState(Date.now);
  const refreshed = Date.parse(sourceRefreshedAt);
  const schedule = scheduledRefresh(product, now, dailySchedule || undefined);
  const valid = Number.isFinite(refreshed);
  const awaiting = valid && dailySchedule !== null && refreshed < schedule.last;
  useEffect(() => {
    let timer: number | undefined;
    function check() {
      const current = Date.now();
      setNow(current);
      const { next } = scheduledRefresh(
        product,
        current,
        dailySchedule || undefined
      );
      timer = window.setTimeout(check, Math.max(1, next - current));
    }
    function resume() {
      window.clearTimeout(timer);
      check();
    }
    check();
    window.addEventListener("focus", resume);
    document.addEventListener("visibilitychange", resume);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("focus", resume);
      document.removeEventListener("visibilitychange", resume);
    };
  }, [product, refreshed, dailySchedule]);
  let notice: string | undefined;
  if (awaiting) {
    notice =
      "Waiting for the updated report. You can still export the current report.";
  }
  return (
    <div>
      <div className="oms-report-card__summary">
        {valid ? (
          <Box>
            <Box display="inline" color="text-body-secondary">
              Data refreshed
            </Box>{" "}
            <strong>
              <time dateTime={sourceRefreshedAt}>
                {reportTimestamp(sourceRefreshedAt)}
              </time>
            </strong>
          </Box>
        ) : (
          <div role="status">
            <Box color="text-body-secondary">
              Report refresh time unavailable.
            </Box>
          </div>
        )}
        {dailySchedule !== null && (
          <Popover
            header="Refresh schedule"
            position="bottom"
            size="large"
            triggerType="text"
            dismissAriaLabel="Close refresh schedule"
            content={
              <SpaceBetween size="xs">
                <KeyValuePairs
                  items={[
                    {
                      label: "Most recent scheduled start",
                      value: (
                        <time dateTime={new Date(schedule.last).toISOString()}>
                          {reportTimestamp(schedule.last)}
                        </time>
                      ),
                    },
                    {
                      label: "Next scheduled start",
                      value: (
                        <time dateTime={new Date(schedule.next).toISOString()}>
                          {reportTimestamp(schedule.next)}
                        </time>
                      ),
                    },
                  ]}
                />
                <Box color="text-body-secondary" fontSize="body-s">
                  These are scheduled start times. Updated data is available
                  after processing finishes.
                </Box>
              </SpaceBetween>
            }
          >
            <span className="oms-report-download__icon-label">
              <span aria-hidden="true">
                <Icon name="calendar" />
              </span>
              <span>Refresh schedule</span>
            </span>
          </Popover>
        )}
        {dailySchedule === null && (
          <Box color="text-body-secondary">
            Refresh schedule not yet confirmed.
          </Box>
        )}
      </div>
      {valid && (
        <div role="status" aria-atomic="true">
          {notice && (
            <div className="oms-report-card__notice">
              <span aria-hidden="true">
                <Icon name="status-info" variant="info" />
              </span>
              <Box color="text-body-secondary" fontSize="body-s">
                {notice}
              </Box>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
